import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { routeModules } from './components/routeModules';

const rootElement = document.getElementById('root');

// Kick off (and wait for) the current page's lazy chunk before we ever call
// hydrateRoot/render. Why: react-snap's prerendered HTML already contains
// the fully-rendered page, but it's plain static markup, not real React SSR
// output — it has none of React's hydration markers. If the matching
// React.lazy() route hasn't finished loading its chunk yet when hydration
// starts, React can't resume that Suspense boundary in place; it drops the
// already-painted content and waits for a fresh client-side fetch of the
// same chunk before repainting. On a slow/throttled connection that shows
// up as a huge Largest Contentful Paint regression, even though First
// Contentful Paint looks fine (that first paint came from the static HTML,
// before it got yanked out from under itself).
//
// Pre-warming the exact chunk the current URL needs means it's already
// resolved (or resolving well ahead of hydration) by the time React
// renders that route, so it hydrates straight through with no flash and no
// second fetch.
function warmCurrentRouteModule() {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  const match = routeModules.find((r) => r.path === path);
  if (!match) return Promise.resolve();

  return Promise.race([
    match.importFn().catch(() => {}),
    // Don't let a slow/broken chunk fetch block first render forever —
    // fall through to a normal (Suspense-fallback-then-content) hydrate.
    new Promise((resolve) => setTimeout(resolve, 4000)),
  ]);
}

function renderApp() {
  if (rootElement.hasChildNodes()) {
    // react-snap prerendered this route to static HTML — hydrate it in
    // place instead of wiping it out with a fresh client render.
    ReactDOM.hydrateRoot(
      rootElement,
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  } else {
    // Dev (`npm start`) or any route react-snap didn't prerender.
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  }
}

warmCurrentRouteModule().then(renderApp);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
